import React, { useState, useEffect } from 'react';
import { knowledgeAPI } from '../services/api';
import colors, { spacing, borderRadius } from '../theme';

/**
 * FlashcardCarousel Component
 *
 * Displays flashcards for missed quiz questions with source context
 * Features: card flip, navigation between cards, dynamic content fetching
 */
function FlashcardCarousel({ flashcards }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    const [sourceContext, setSourceContext] = useState(null);
    const [loadingContext, setLoadingContext] = useState(false);

    const currentCard = flashcards[currentIndex];

    // Load source context when card changes or is flipped to back
    useEffect(() => {
        if (currentCard && isFlipped && !sourceContext) {
            loadSourceContext();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCard, isFlipped]);

    // Reset when navigating to new card
    useEffect(() => {
        setIsFlipped(false);
        setSourceContext(null);
    }, [currentIndex]);

    const loadSourceContext = async () => {
        setLoadingContext(true);
        try {
            let content = null;

            console.log('Loading content for card:', {
                knowledgeId: currentCard.knowledgeId,
                isNotionSynced: currentCard.isNotionSynced,
                isConfluenceSynced: currentCard.isConfluenceSynced,
                sectionHeading: currentCard.sectionHeading
            });

            // Fetch content based on sync type
            if (currentCard.isNotionSynced) {
                console.log('Fetching Notion content...');
                const response = await knowledgeAPI.getNotionContent(currentCard.knowledgeId);
                content = response.data.data.content; // API returns { success, data: { content } }
            } else if (currentCard.isConfluenceSynced) {
                console.log('Fetching Confluence content...');
                const response = await knowledgeAPI.getConfluenceContent(currentCard.knowledgeId);
                content = response.data.data.content; // API returns { success, data: { content } }
            } else {
                console.log('Fetching regular content...');
                const response = await knowledgeAPI.getOne(currentCard.knowledgeId);
                content = response.data.data.content; // API returns { success, data: entry }
            }

            console.log('Content loaded, length:', content ? content.length : 0);

            // Extract relevant section based on sectionHeading
            const extractedContext = extractRelevantSection(content, currentCard.sectionHeading);
            console.log('Extracted context length:', extractedContext ? extractedContext.length : 0);
            setSourceContext(extractedContext);

        } catch (error) {
            console.error('Error loading source context:', error);
            console.error('Error details:', error.response?.data || error.message);
            setSourceContext(`Unable to load source content. ${error.response?.data?.error || error.message || 'Please try again.'}`);
        } finally {
            setLoadingContext(false);
        }
    };

    /**
     * Extracts the relevant section from content based on heading
     */
    const extractRelevantSection = (content, sectionHeading) => {
        if (!content || content.trim().length === 0) {
            return 'Content not available. This question was created from synced content that may have been moved or deleted.';
        }
        if (!sectionHeading) {
            // If no section heading, return first 300 words
            const words = content.split(/\s+/).slice(0, 300);
            return words.join(' ') + (content.split(/\s+/).length > 300 ? '...' : '');
        }

        // Try to find the section with this heading
        const lines = content.split('\n');
        let sectionStart = -1;
        let sectionEnd = -1;

        // Clean heading for matching (remove # symbols)
        const cleanHeading = sectionHeading.replace(/^#+\s*/, '').trim().toLowerCase();

        // Find the heading
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();
            const cleanLine = line.replace(/^#+\s*/, '').trim().toLowerCase();

            if (cleanLine === cleanHeading || cleanLine.includes(cleanHeading)) {
                sectionStart = i;
                break;
            }
        }

        if (sectionStart === -1) {
            // Heading not found, search for keywords in content
            const keywords = cleanHeading.split(/\s+/);
            for (let i = 0; i < lines.length; i++) {
                const lineLower = lines[i].toLowerCase();
                if (keywords.some(keyword => lineLower.includes(keyword))) {
                    sectionStart = i;
                    break;
                }
            }
        }

        if (sectionStart === -1) {
            // Still not found, return beginning of content
            const words = content.split(/\s+/).slice(0, 200);
            return words.join(' ') + '...';
        }

        // Find the end of this section (next heading or end of content)
        for (let i = sectionStart + 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (line.match(/^#{1,6}\s+/)) {
                // Found next heading
                sectionEnd = i;
                break;
            }
        }

        if (sectionEnd === -1) {
            sectionEnd = Math.min(lines.length, sectionStart + 20); // Take next 20 lines max
        }

        const sectionLines = lines.slice(sectionStart, sectionEnd);
        return sectionLines.join('\n').trim();
    };

    const handleNext = () => {
        setCurrentIndex((prev) => (prev + 1) % flashcards.length);
    };

    const handlePrevious = () => {
        setCurrentIndex((prev) => (prev - 1 + flashcards.length) % flashcards.length);
    };

    const handleFlip = () => {
        setIsFlipped(!isFlipped);
    };

    if (flashcards.length === 0) {
        return null;
    }

    return (
        <div>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: spacing.sm
            }}>
                <h4 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: colors.primary,
                    margin: 0
                }}>
                    📚 Flashcards
                </h4>
                <div style={{
                    fontSize: '0.75rem',
                    color: colors.gray[600],
                    fontWeight: '500'
                }}>
                    {currentIndex + 1} of {flashcards.length}
                </div>
            </div>

            {/* Flashcard */}
            <div style={{
                position: 'relative',
                height: '160px',
                perspective: '1000px',
                marginBottom: spacing.sm,
                overflow: 'hidden',
                filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.07))'
            }}>
                <div
                    onClick={handleFlip}
                    style={{
                        position: 'relative',
                        width: '100%',
                        height: '160px',
                        transformStyle: 'preserve-3d',
                        transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                        cursor: 'pointer'
                    }}
                >
                    {/* Front of card - Question */}
                    <div style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        backfaceVisibility: 'hidden',
                        backgroundColor: colors.blue[50],
                        border: `2px solid ${colors.primary}`,
                        borderRadius: borderRadius.md,
                        padding: spacing.md,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        boxSizing: 'border-box'
                    }}>
                        <div style={{
                            fontSize: '0.625rem',
                            color: colors.gray[600],
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            marginBottom: spacing.xs
                        }}>
                            Question
                        </div>
                        <div style={{
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            color: colors.primary,
                            marginBottom: spacing.xs
                        }}>
                            {currentCard.question}
                        </div>
                        <div style={{
                            fontSize: '0.75rem',
                            color: colors.green[700],
                            fontWeight: '600',
                            marginTop: spacing.xs
                        }}>
                            ✓ {currentCard.correctAnswerText}
                        </div>
                        <div style={{
                            fontSize: '0.625rem',
                            color: colors.gray[500],
                            marginTop: spacing.sm,
                            fontStyle: 'italic'
                        }}>
                            Click for source →
                        </div>
                    </div>

                    {/* Back of card - Source Context */}
                    <div style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        backfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                        backgroundColor: colors.green[50],
                        border: `2px solid ${colors.green[500]}`,
                        borderRadius: borderRadius.md,
                        padding: spacing.md,
                        display: 'flex',
                        flexDirection: 'column',
                        overflowY: 'auto',
                        boxSizing: 'border-box'
                    }}>
                        <div style={{
                            fontSize: '0.625rem',
                            color: colors.gray[600],
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            marginBottom: spacing.xs
                        }}>
                            Source{currentCard.sectionHeading && `: ${currentCard.sectionHeading}`}
                        </div>

                        {loadingContext ? (
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                color: colors.gray[600],
                                fontSize: '0.75rem'
                            }}>
                                Loading...
                            </div>
                        ) : (
                            <div style={{
                                fontSize: '0.75rem',
                                color: colors.gray[800],
                                lineHeight: '1.4',
                                whiteSpace: 'pre-wrap',
                                overflow: 'auto'
                            }}>
                                {sourceContext || 'No source context available.'}
                            </div>
                        )}

                        <div style={{
                            fontSize: '0.625rem',
                            color: colors.gray[500],
                            marginTop: spacing.xs,
                            fontStyle: 'italic'
                        }}>
                            Click to flip →
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: spacing.xs
            }}>
                <div style={{
                    fontSize: '0.625rem',
                    color: colors.gray[500],
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}>
                    {currentCard.knowledgeTitle}
                </div>

                <div style={{ display: 'flex', gap: spacing.xs }}>
                    <button
                        onClick={handlePrevious}
                        disabled={flashcards.length === 1}
                        style={{
                            padding: '4px 10px',
                            backgroundColor: colors.white,
                            color: colors.primary,
                            border: `1px solid ${colors.primary}`,
                            borderRadius: borderRadius.sm,
                            fontSize: '0.625rem',
                            fontWeight: '600',
                            cursor: flashcards.length === 1 ? 'not-allowed' : 'pointer',
                            opacity: flashcards.length === 1 ? 0.5 : 1
                        }}
                    >
                        ←
                    </button>

                    <button
                        onClick={handleNext}
                        disabled={flashcards.length === 1}
                        style={{
                            padding: '4px 10px',
                            backgroundColor: colors.primary,
                            color: colors.white,
                            border: `1px solid ${colors.primary}`,
                            borderRadius: borderRadius.sm,
                            fontSize: '0.625rem',
                            fontWeight: '600',
                            cursor: flashcards.length === 1 ? 'not-allowed' : 'pointer',
                            opacity: flashcards.length === 1 ? 0.5 : 1
                        }}
                    >
                        →
                    </button>
                </div>
            </div>
        </div>
    );
}

export default FlashcardCarousel;
